/**
 * Handles text drag end operation
 * @param {string} id - Text ID
 * @param {number} x - New x position
 * @param {number} y - New y position
 * @param {Array} texts - Current texts array
 * @param {Function} setTexts - Function to set texts
 * @param {Function} setSelectedText - Function to set selected text
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @param {Object} currentText - Optional: pass current text object to preserve all properties (rotation, scaleX, scaleY, etc.)
 */
export const handleTextDragEnd = (
  id,
  x,
  y,
  texts,
  setTexts,
  setSelectedText,
  saveToHistory,
  isUndoingRedoing,
  currentText = null // Optional: pass current text object to preserve all properties (rotation, scaleX, scaleY, etc.)
) => {
  // Safety check: ensure texts is an array
  if (!Array.isArray(texts)) {
    console.warn("handleTextDragEnd: texts is not an array", texts);
    return;
  }

  // CRITICAL: Use functional update to get the current state, avoiding stale closure issues
  // This ensures we always work with the most up-to-date texts array
  setTexts((prevTexts) => {
    // CRITICAL: Find the current text to preserve all properties
    // If currentText is provided, use it; otherwise find it from the current state
    // This ensures we preserve rotation, scaleX, scaleY, and all other properties
    const existingText =
      currentText ||
      prevTexts.find((text) => text.id === id || text.shape_id === id);
    
    if (!existingText) {
      console.warn("handleTextDragEnd: text not found", id);
      return prevTexts; // Return unchanged if text not found
    }

    // CRITICAL: Preserve ALL properties from the existing text (rotation, scaleX, scaleY, etc.)
    // Only update x, y - everything else should be preserved
    const width =
      existingText.width ??
      existingText.canvas_page_location_and_size?.width;
    const height =
      existingText.height ??
      existingText.canvas_page_location_and_size?.height;
    const loc = existingText.canvas_page_location_and_size;

    const updatedTexts = prevTexts.map((text) =>
      text.id === id || text.shape_id === id
        ? {
            ...existingText, // Preserve all existing properties (rotation, scaleX, scaleY, etc.)
            x,
            y,
            _previewTargetStage: undefined,
            _isDragging: undefined,
            ...(loc
              ? {
                  canvas_page_location_and_size: {
                    ...loc,
                    x,
                    y,
                    ...(width != null ? { width } : {}),
                    ...(height != null ? { height } : {}),
                  },
                }
              : width != null && height != null
                ? {
                    canvas_page_location_and_size: {
                      x,
                      y,
                      width,
                      height,
                      rotation: existingText.rotation ?? 0,
                    },
                  }
                : {}),
            _lastUpdated: Date.now(),
            _isInOffCanvasDuringDrag: undefined, // Clear the drag flag
          }
        : text
    );
    
    // Debug logging to verify rotation is preserved
    const updatedText = updatedTexts.find(
      (text) => text.id === id || text.shape_id === id,
    );
    if (updatedText) {
    }

    // Update selectedText to reflect the new position
    if (setSelectedText) {
      setSelectedText((prev) => {
        if (prev?.id === id || prev?.shape_id === id) {
          return {
            ...prev,
            x,
            y,
            _lastUpdated: Date.now(),
            _isInOffCanvasDuringDrag: undefined,
            _previewTargetStage: undefined,
            _isDragging: undefined,
          };
        }
        return prev;
      });
    }

    // Save to history with the updated texts
    // CRITICAL: Call saveToHistory OUTSIDE the setTexts callback to avoid React batching issues
    // Use setTimeout to ensure it's called after the state update completes
    if (saveToHistory) {
      // Check if we're currently undoing/redoing to prevent clearing redo stack
      if (isUndoingRedoing && isUndoingRedoing.current) {
        return updatedTexts;
      }

      // CRITICAL: Save drag operations immediately, not when element is unselected
      // This ensures drag operations create separate history entries that can be undone independently
      // Pass the updated texts array as override to ensure saveToHistory gets the correct state
      setTimeout(() => {
        saveToHistory("text_drag_end", null, null, null, null, updatedTexts);
      }, 0);
    }

    return updatedTexts;
  });
};

/**
 * Create a function to handle text drag end with bound dependencies
 * @param {Array} texts - Current texts array
 * @param {Function} setTexts - Function to set texts
 * @param {Function} setSelectedText - Function to set selected text
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @returns {Function} Function to handle text drag end
 */
// Track last drag end call to prevent duplicates
let lastTextDragEndCall = null;
const TEXT_DRAG_END_DEBOUNCE_MS = 50; // 50ms debounce window

export const createHandleTextDragEnd = (
  texts,
  setTexts,
  setSelectedText,
  saveToHistory,
  isUndoingRedoing
) => {
  return (id, x, y, mouseClientX = null, mouseClientY = null, currentText = null) => {
    // CRITICAL: Debounce duplicate calls to prevent creating duplicate history entries
    // This handles cases where handleTextDragEnd is called twice (e.g., from React strict mode or multiple event handlers)
    const now = Date.now();
    const callKey = `${id}-${x}-${y}`;
    
    if (
      lastTextDragEndCall &&
      lastTextDragEndCall.key === callKey &&
      now - lastTextDragEndCall.timestamp < TEXT_DRAG_END_DEBOUNCE_MS
    ) {
      return; // Skip duplicate call
    }
    
    // Update last call tracking
    lastTextDragEndCall = {
      key: callKey,
      timestamp: now,
    };
    
    return handleTextDragEnd(
      id,
      x,
      y,
      texts,
      setTexts,
      setSelectedText,
      saveToHistory,
      isUndoingRedoing,
      currentText // Pass the current text object to preserve all properties (rotation, scaleX, scaleY, etc.)
    );
  };
};

